import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { Tab, Tabs } from "@mui/material";
import { DataGrid, ptBR, GridColDef } from "@mui/x-data-grid";
import { TextH2 } from "../../../styles/global.styles";
import { IBanner, IRestPaginate } from "../../../Services/Interfaces";
import { ImgSgv } from "../../Login/stylesLogin";
import { useNavigate } from "react-router-dom";
import { digitalApiUrl, digitalAssetsUrl } from "../../../Services/Api";
import {
  deleteBanner,
  getBannerAll,
  putBanner,
  reviewBannerArt,
  BannerScope,
} from "../../../Services/Banner";
import Swal from "sweetalert2";

/**
 * Três origens de banner convivem na mesma tabela e têm regras diferentes:
 *
 *   institucional → criado aqui pelo admin. Vai ao ar direto, tem país e
 *                   ordem de exibição, e é editável nesta tela.
 *   showcase      → arte enviada por estabelecimento nos planos Pulse e Wave.
 *                   Só entra na vitrine depois de aprovada, e o prazo
 *                   contratado começa a contar na aprovação.
 *   connection    → arte enviada por estabelecimento no plano Connection.
 *                   Passa pela mesma análise, mas sem prazo de veiculação.
 *
 * A separação por aba existe porque o critério de análise muda conforme o
 * plano — e porque misturar tudo numa grade só deixava a coluna "Arte"
 * aparecendo em linha que não tem arte para aprovar.
 */
const ABAS: { scope: BannerScope; label: string; aviso?: string }[] = [
  { scope: "institutional", label: "Banners institucionais" },
  {
    scope: "showcase",
    label: "Banners plano Showcase",
    aviso:
      "Artes enviadas por estabelecimentos nos planos Pulse e Wave. Ao aprovar, a arte entra na vitrine e o prazo contratado (15 dias no Pulse, 30 no Wave) começa a contar naquele momento.",
  },
  {
    scope: "connection",
    label: "Banners plano Connection",
    aviso:
      "Artes enviadas por estabelecimentos no plano Connection. Ao aprovar, a arte entra no ar e permanece enquanto a assinatura estiver ativa.",
  },
];

export const Banner = () => {
  const navigate = useNavigate();

  const Rota = (path: string) => {
    return navigate(`${path}`, { replace: true });
  };

  const [abaAtual, setAbaAtual] = useState(0);
  const [banners, setBanners] = useState<IBanner[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });

  const scope = ABAS[abaAtual].scope;
  const avisoDaAba = ABAS[abaAtual].aviso;

  // Showcase e Connection compartilham a mesma grade: as duas são arte
  // enviada pelo estabelecimento e passam pela mesma análise.
  const isInstitucional = scope === "institutional";

  const carregar = useCallback(() => {
    setIsLoading(true);
    getBannerAll(
      paginationModel.page * paginationModel.pageSize,
      paginationModel.pageSize,
      scope
    )
      .then((response) => {
        const { data } = response as IRestPaginate;
        setRowCount(data.pagination.total);
        setBanners(data.data);
      })
      .finally(() => setIsLoading(false));
  }, [paginationModel.page, paginationModel.pageSize, scope]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Recarrega a lista em vez de dar reload na página: com abas, o reload
  // devolveria o admin para a primeira aba a cada aprovação.
  const recarregar = () => carregar();

  const trocarAba = (_: React.SyntheticEvent, novaAba: number) => {
    setAbaAtual(novaAba);
    setPaginationModel((atual) => ({ ...atual, page: 0 }));
  };

  // ── Análise da arte ──────────────────────────────────────────────────────
  // Extraído em funções porque as mesmas ações aparecem em dois lugares: nos
  // botões da linha e dentro do preview da imagem.

  const urlDaArte = (filename?: string | null) =>
    filename ? digitalApiUrl + filename : digitalAssetsUrl + "/assets/banner.jpg";

  const aprovarArte = (id: string) =>
    Swal.fire({
      title: "Aprovar esta arte?",
      text: "Ela entra na vitrine imediatamente e o prazo contratado começa a contar agora.",
      showDenyButton: true,
      showCancelButton: false,
      confirmButtonText: "Aprovar",
      denyButtonText: "Cancelar",
      confirmButtonColor: "#63adb7",
      denyButtonColor: "#b96ea5",
    }).then((result) => {
      if (result.isConfirmed) {
        reviewBannerArt(id, "approved").then(recarregar);
      }
    });

  const recusarArte = (id: string) =>
    Swal.fire({
      title: "Recusar arte",
      input: "textarea",
      inputLabel: "Motivo (será enviado ao anunciante por e-mail)",
      inputPlaceholder: "Ex.: a resolução está abaixo do mínimo exigido...",
      inputAttributes: { "aria-label": "Motivo da recusa" },
      showCancelButton: true,
      confirmButtonText: "Recusar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#b96ea5",
      inputValidator: (value) =>
        !value || value.trim().length < 5
          ? "Descreva o que precisa ser ajustado."
          : null,
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        reviewBannerArt(id, "rejected", result.value).then(recarregar);
      }
    });

  /**
   * Preview da arte em tamanho grande, com as ações ali dentro.
   *
   * Quem está moderando olha a imagem e decide na hora — obrigar a fechar o
   * modal para clicar em Aprovar na linha só atrapalha.
   */
  const verArte = (row: any) => {
    const pendente = (row.artStatus ?? "pending") === "pending";
    const estabelecimento = row.userEstabilishment?.estabilishment?.name;

    Swal.fire({
      title: row.name,
      text: estabelecimento ? `Enviado por ${estabelecimento}` : undefined,
      imageUrl: urlDaArte(row.filename),
      imageAlt: row.title ?? row.name,
      imageWidth: 640,
      width: 720,
      showConfirmButton: pendente,
      showDenyButton: pendente,
      showCancelButton: true,
      confirmButtonText: "Aprovar",
      denyButtonText: "Recusar",
      cancelButtonText: "Fechar",
      confirmButtonColor: "#63adb7",
      denyButtonColor: "#b96ea5",
      footer: row.filename
        ? `<a href="${urlDaArte(row.filename)}" target="_blank" rel="noreferrer">Abrir imagem em tamanho original</a>`
        : undefined,
    }).then((result) => {
      if (result.isConfirmed) {
        aprovarArte(row.id);
      } else if (result.isDenied) {
        recusarArte(row.id);
      }
    });
  };

  // ── Colunas comuns às duas abas ──────────────────────────────────────────

  const colunaNome: GridColDef = {
    field: "name",
    headerName: "Nome",
    type: "string",
    minWidth: window.innerWidth / 10,
  };

  const colunaTitulo: GridColDef = {
    field: "title",
    headerName: "Titulo",
    type: "string",
    minWidth: window.innerWidth / 10,
  };

  const colunaStatus: GridColDef = {
    field: "isActive",
    headerName: "Status",
    minWidth: 160,
    renderCell: (params) => {
      return params.row.isActive ? (
        <button
          onClick={() => {
            Swal.fire({
              title: "Tem certeza que deseja desativar o Banner?",
              showDenyButton: true,
              showCancelButton: false,
              confirmButtonText: "Sim",
              denyButtonText: "Não",
              confirmButtonColor: "#63adb7",
              denyButtonColor: "#b96ea5",
            }).then((result) => {
              if (result.isConfirmed) {
                putBanner(params.row.id, { isActive: "inactive" }).then(
                  recarregar
                );
              }
            });
          }}
          style={{
            backgroundColor: "gray",
            border: "none",
            cursor: "pointer",
            color: "#FFFFFF",
            fontWeight: "bold",
            height: "80%",
            width: "90%",
            borderRadius: "10px",
          }}
        >
          Desativar
        </button>
      ) : (
        <button
          onClick={() => {
            Swal.fire({
              title: "Tem certeza que deseja Ativar o Banner?",
              showDenyButton: true,
              showCancelButton: false,
              confirmButtonText: "Sim",
              denyButtonText: "Não",
              confirmButtonColor: "#63adb7",
              denyButtonColor: "#b96ea5",
            }).then((result) => {
              if (result.isConfirmed) {
                putBanner(params.row.id, { isActive: "active" }).then(
                  recarregar
                );
              }
            });
          }}
          style={{
            backgroundColor: "#63adb7",
            border: "none",
            cursor: "pointer",
            color: "#FFFFFF",
            fontWeight: "bold",
            height: "80%",
            width: "90%",
            borderRadius: "10px",
          }}
        >
          Ativar
        </button>
      );
    },
  };

  const colunaExcluir: GridColDef = {
    field: "excluir",
    headerName: "Excluir",
    minWidth: 140,
    sortable: false,
    renderCell: (params) => {
      return (
        <button
          onClick={() => {
            Swal.fire({
              title: "Tem certeza que deseja excluir o Banner?",
              showDenyButton: true,
              showCancelButton: false,
              confirmButtonText: "Sim",
              denyButtonText: "Não",
              confirmButtonColor: "#b96e8b",
              denyButtonColor: "#63adb7",
            }).then((result) => {
              if (result.isConfirmed) {
                deleteBanner(params.row.id).then(recarregar);
              }
            });
          }}
          style={{
            backgroundColor: "#b96ea5",
            border: "none",
            cursor: "pointer",
            color: "#FFFFFF",
            fontWeight: "bold",
            height: "80%",
            width: "90%",
            borderRadius: "10px",
          }}
        >
          Excluir
        </button>
      );
    },
  };

  // ── Colunas só da aba institucional ──────────────────────────────────────

  const colunasInstitucional: GridColDef[] = [
    colunaNome,
    colunaTitulo,
    {
      field: "displayOrder",
      headerName: "Ordenação",
      type: "string",
      minWidth: 110,
    },
    {
      field: "line",
      headerName: "Linha",
      type: "string",
      minWidth: 90,
    },
    {
      field: "countries",
      headerName: "Países",
      minWidth: 180,
      sortable: false,
      renderCell: (params) => {
        // Sem país nenhum o banner é global: aparece para qualquer usuário.
        const nomes: string[] = (params.row.countries ?? [])
          .map((c: { name?: string }) => c?.name)
          .filter(Boolean);

        if (nomes.length === 0) {
          return <span style={{ fontSize: 13, color: "#6b7280" }}>Todos</span>;
        }

        // A grid não quebra linha: com muitos países a célula viraria uma
        // tira ilegível, então mostra os dois primeiros e conta o resto.
        // O title deixa a lista inteira a um hover de distância.
        const visiveis = nomes.slice(0, 2).join(", ");
        const resto = nomes.length - 2;

        return (
          <span style={{ fontSize: 13 }} title={nomes.join(", ")}>
            {resto > 0 ? `${visiveis} +${resto}` : visiveis}
          </span>
        );
      },
    },
    colunaStatus,
    colunaExcluir,
    {
      field: "edit",
      headerName: "Visualizar",
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        return (
          <button
            onClick={() => Rota(params.row.id)}
            style={{
              backgroundColor: "#63adb7",
              border: "none",
              cursor: "pointer",
              color: "#FFFFFF",
              fontWeight: "bold",
              height: "80%",
              width: "100%",
              borderRadius: "10px",
              alignContent: "center",
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <ImgSgv
              style={{ margin: "5px" }}
              width={18}
              src="/icons/menu/gerenciamento.svg"
            />
            Editar
          </button>
        );
      },
    },
  ];

  // ── Colunas só da aba showcase ───────────────────────────────────────────

  const colunasShowcase: GridColDef[] = [
    colunaNome,
    {
      field: "estabilishment",
      headerName: "Estabelecimento",
      minWidth: window.innerWidth / 10,
      sortable: false,
      renderCell: (params) => {
        const nome = params.row.userEstabilishment?.estabilishment?.name;

        return nome ? (
          <span style={{ fontSize: 13 }}>{nome}</span>
        ) : (
          <span style={{ fontSize: 13, color: "#6b7280" }}>—</span>
        );
      },
    },
    {
      field: "endereco",
      headerName: "Endereço",
      minWidth: window.innerWidth / 6,
      sortable: false,
      renderCell: (params) => {
        const endereco =
          params.row.userEstabilishment?.estabilishment?.addressInGoogleFormat;

        // title no elemento: endereço longo é cortado pela largura da coluna,
        // e o texto inteiro fica no hover
        return endereco ? (
          <span
            title={endereco}
            style={{
              fontSize: 13,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {endereco}
          </span>
        ) : (
          <span style={{ fontSize: 13, color: "#6b7280" }}>—</span>
        );
      },
    },
    {
      field: "filename",
      headerName: "Arte enviada",
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <button
          onClick={() => verArte(params.row)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "5px 12px",
            borderRadius: 8,
            border: "1.5px solid #63adb7",
            background: "#fff",
            color: "#63adb7",
            fontWeight: 700,
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          <img
            src={urlDaArte(params.row.filename)}
            alt=""
            width={38}
            height={14}
            style={{ objectFit: "cover", borderRadius: 3 }}
          />
          Ver arte
        </button>
      ),
    },
    {
      field: "artStatus",
      headerName: "Arte",
      minWidth: 240,
      sortable: false,
      renderCell: (params) => {
        const status = params.row.artStatus ?? "pending";

        // Já aprovada: mostra desde quando está no ar. O prazo contratado
        // conta a partir dessa data, não da criação do banner.
        if (status === "approved") {
          const desde = params.row.artApprovedAt
            ? new Date(
                String(params.row.artApprovedAt).replace(" ", "T")
              ).toLocaleDateString("pt-BR")
            : null;
          return (
            <span style={{ color: "#1a7a4a", fontWeight: 600, fontSize: 13 }}>
              No ar{desde ? ` desde ${desde}` : ""}
            </span>
          );
        }

        // Recusada: mostra o motivo enviado ao anunciante
        if (status === "rejected") {
          return (
            <span
              title={params.row.artRejectionReason ?? ""}
              style={{ color: "#b91c1c", fontWeight: 600, fontSize: 13 }}
            >
              Recusada — aguardando reenvio
            </span>
          );
        }

        // Pendente: ações de aprovar / recusar
        return (
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={() => aprovarArte(params.row.id)}
              style={{
                padding: "5px 12px",
                borderRadius: 8,
                border: "none",
                background: "#63adb7",
                color: "#fff",
                fontWeight: 700,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Aprovar
            </button>

            <button
              onClick={() => recusarArte(params.row.id)}
              style={{
                padding: "5px 12px",
                borderRadius: 8,
                border: "1.5px solid #b96ea5",
                background: "#fff",
                color: "#b96ea5",
                fontWeight: 700,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Recusar
            </button>
          </div>
        );
      },
    },
    colunaStatus,
    colunaExcluir,
  ];

  const columns = useMemo(
    () => (isInstitucional ? colunasInstitucional : colunasShowcase),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isInstitucional]
  );

  return (
    <>
      <div
        style={{
          alignContent: "space-between",
          alignItems: "space-between",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <TextH2
          style={{
            alignItems: "center",
            display: "flex",
            justifyContent: "start",
            textAlign: "center",
            width: "100%",
          }}
        >
          <ImgSgv width={24} src="/icons/menu/visao.svg" />
          &nbsp;Banner
        </TextH2>

        {/* Cadastrar só faz sentido no institucional: nas outras abas a arte
            é enviada pelo próprio estabelecimento, no painel dele. */}
        {isInstitucional && (
          <button
            onClick={() => Rota("New")}
            style={{
              backgroundColor: "#9c9b9f",
              border: "none",
              cursor: "pointer",
              color: "#FFFFFF",
              fontWeight: "bold",
              height: "56px",
              width: "128px",
              borderRadius: "10px",
              paddingRight: "10px",
              marginRight: "20px",
              alignContent: "center",
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <ImgSgv
              style={{ marginRight: "5px", marginLeft: "10px" }}
              width={18}
              src="/icons/menu/new.svg"
            />
            Cadastrar
          </button>
        )}
      </div>

      <Tabs
        value={abaAtual}
        onChange={trocarAba}
        textColor="inherit"
        TabIndicatorProps={{ style: { backgroundColor: "#63adb7" } }}
        sx={{
          borderBottom: "1px solid #e8e8e8",
          marginBottom: "12px",
          "& .Mui-selected": { color: "#63adb7", fontWeight: 700 },
        }}
      >
        {ABAS.map((aba) => (
          <Tab
            key={aba.scope}
            label={aba.label}
            sx={{ textTransform: "none", fontSize: 14 }}
          />
        ))}
      </Tabs>

      {avisoDaAba && (
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#888" }}>
          {avisoDaAba}
        </p>
      )}

      <Box sx={{ height: "85%", width: "100%" }}>
        <DataGrid
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          rows={banners}
          columns={columns}
          rowCount={rowCount}
          loading={isLoading}
          pageSizeOptions={[20, 50, 100]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
        />
      </Box>
    </>
  );
};